// FCP design system — theme management (v2.8.0)
//
// Two concerns:
//   1. Mode: light / dark / auto. User preference. Persisted to localStorage.
//      Applied as `data-mode="light"|"dark"` on <html>.
//   2. Preset: default / slate / mono. Admin preference, comes from
//      settings.appearance_preset. Applied as `data-preset="..."` on <html>.
//
// Brand color (settings.brand_color) is injected as --color-brand on
// the document root. We also compute --color-brand-text (white or near-
// black depending on luminance) so a primary CTA is always readable
// regardless of which color the admin picks.
//
// useTheme() returns { mode, resolvedMode, setMode, preset, brandColor }.
// resolvedMode is the actual rendered mode after 'auto' is resolved
// against the OS preference — the value the UI should use to render
// mode-specific things (e.g. icon swap on the toggle button).

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const ThemeContext = createContext(null);

export const PRESETS = ['default', 'slate', 'mono'];
const MODE_STORAGE_KEY = 'fcp_theme_mode';
const VALID_MODES = ['light', 'dark', 'auto'];

/**
 * Compute readable on-brand text color from a brand color hex.
 * Returns '#0a0a0a' (near-black) or '#ffffff' (white) — whichever has
 * the better WCAG contrast against the brand color.
 *
 * Why contrast ratio rather than a luminance threshold: a fixed threshold
 * picks "white text" for vivid mid-luminance colors like #2fc511 (lime
 * green) where dark text is actually more readable in practice. The
 * contrast formula picks the better option deterministically.
 */
export function deriveOnBrandText(hex) {
  if (!hex || typeof hex !== 'string') return '#ffffff';
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6 || !/^[0-9a-f]{6}$/i.test(h)) return '#ffffff';
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const toLin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  // WCAG contrast ratio: (L_lighter + 0.05) / (L_darker + 0.05).
  // Compare against near-white (#ffffff = L 1.0) and near-black (#0a0a0a ≈ L 0.0034).
  const L_white = 1.0;
  const L_dark = 0.00335; // sRGB → linear of #0a0a0a/255
  const contrastWhite = (L_white + 0.05) / (L + 0.05);
  const contrastDark = (L + 0.05) / (L_dark + 0.05);
  return contrastDark >= contrastWhite ? '#0a0a0a' : '#ffffff';
}

/**
 * Read the OS-level light/dark preference. Returns 'light' or 'dark'.
 */
function getSystemMode() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Read persisted user preference. Returns 'auto' if none stored.
 */
function getStoredMode() {
  if (typeof window === 'undefined') return 'auto';
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    return VALID_MODES.includes(v) ? v : 'auto';
  } catch (_e) {
    return 'auto';
  }
}

export function ThemeProvider({ children, brandColor, preset }) {
  const [mode, setModeState] = useState(getStoredMode);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // Track OS preference changes. If user is on 'auto' the rendered mode
  // updates live; if they've picked 'light'/'dark' explicitly the system
  // change is recorded but doesn't change rendering.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari < 14
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  const resolvedMode = mode === 'auto' ? systemMode : mode;

  // Apply data-mode + data-preset on <html>. Token CSS keys off these.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-mode', resolvedMode);
  }, [resolvedMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const safe = PRESETS.includes(preset) ? preset : 'default';
    document.documentElement.setAttribute('data-preset', safe);
  }, [preset]);

  // Inject brand color + derived on-brand text color.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (brandColor) {
      root.style.setProperty('--color-brand', brandColor);
      root.style.setProperty('--color-brand-text', deriveOnBrandText(brandColor));
    } else {
      // Defensive: clear inline override so the preset's fallback shows.
      root.style.removeProperty('--color-brand');
      root.style.removeProperty('--color-brand-text');
    }
  }, [brandColor]);

  const setMode = useCallback((next) => {
    if (!VALID_MODES.includes(next)) return;
    setModeState(next);
    try { localStorage.setItem(MODE_STORAGE_KEY, next); } catch (_e) {}
  }, []);

  const value = useMemo(() => ({
    mode,
    resolvedMode,
    setMode,
    preset: PRESETS.includes(preset) ? preset : 'default',
    brandColor: brandColor || null,
  }), [mode, resolvedMode, setMode, preset, brandColor]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
